<script setup lang="ts">
import { onMounted } from 'vue';

import { useStatsigEventsStore } from '@apps/main/new-desktop/core/stores';

import { TransportType } from '@libs/trip-planner-interfaces';

import type { AlternativeItineraryFlightSegment } from '../../../models';
import { useTripOverviewPageStore } from '../../../stores';
import ChangeProductLink from '../ChangeProductLink.vue';
import ItineraryTimeline from '../ItineraryTimeline.vue';
import ItineraryTransportSelector from '../ItineraryTransportSelector.vue';

import AlternativeFlightProductCard from './AlternativeFlightProductCard.vue';
import FlightSegmentHeader from './FlightSegmentHeader.vue';

interface AlternativeFlightSegmentProps {
  segment: AlternativeItineraryFlightSegment;
}

const props = defineProps<AlternativeFlightSegmentProps>();

interface AlternativeFlightSegmentEmits {
  (event: 'show-details'): void;
  (event: 'confirm-alternative'): void;
  (eventName: 'remove-filter', id: string): void;
  (eventName: 'show-more-filters'): void;
}

const emit = defineEmits<AlternativeFlightSegmentEmits>();

const statsigEventsStore = useStatsigEventsStore();
const tripOverviewPageStore = useTripOverviewPageStore();

onMounted(() => {
  statsigEventsStore.logTripOverviewAlternativeFlightDisplayed();
});
</script>

<template>
  <ItineraryTimeline :segment-type="props.segment.segmentType">
    <template #dropdown>
      <ItineraryTransportSelector
        :transport-type="TransportType.Flight"
        :disabled="false"
        @select-transport="
          tripOverviewPageStore.selectTransport(segment, $event)
        "
      />
    </template>

    <template #header>
      <FlightSegmentHeader
        :segment="props.segment"
        :print-mode="false"
        @show-more-filters="emit('show-more-filters')"
        @remove-filter="emit('remove-filter', $event)"
      />
    </template>

    <template #content>
      <div class="alternative-flight-segment-content-wrapper">
        <AlternativeFlightProductCard
          :product-card-data="props.segment.productCardData"
          @show-details="emit('show-details')"
          @confirm-alternative="emit('confirm-alternative')"
        />

        <ChangeProductLink
          :link-context="{ segmentType: props.segment.segmentType }"
          class="change-product-link"
        />
      </div>
    </template>
  </ItineraryTimeline>
</template>

<style scoped lang="scss">
.alternative-flight-segment-content-wrapper {
  display: flex;
  flex-flow: column;
  gap: var(--spacing-12);

  .change-product-link {
    align-self: flex-end;
  }
}

.alternative-flight-segment-header {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: var(--spacing-12);

  .separator {
    border-right: 1px solid var(--color-neutral-30);
    height: 100%;
  }

  .suggestion-type {
    color: var(--color-neutral-20);
    border: none;
    @include font-label-s-light;
  }

  @include for-less-or-equal-than-599-px {
    width: 100%;
    .separator {
      display: none;
    }
  }
}
</style>
